'use client';

import { type ReactElement, useState } from 'react';
import type { Matcher } from 'react-day-picker';

import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from './button';
import { Calendar } from './calendar';
import { datePickerPanel } from './calendar-styles';
import { type DateRange, formatRangeSummary } from './date-format';

/** Figma Date Picker "Type": one day, or a start–end range. */
export type DatePickerSelection =
  | {
      type?: 'single';
      value?: Date;
      /** Called when a day is picked. */
      onValueChange?: (value: Date | undefined) => void;
    }
  | {
      type: 'range';
      value?: DateRange;
      /** Called with the draft range when the user presses Apply. */
      onValueChange?: (value: DateRange | undefined) => void;
    };

/** Everything but the selection. */
export type DatePickerOptions = {
  /** Days that can't be picked, e.g. `{ before: today }` for scheduling. */
  disabled?: Matcher | Matcher[];
  /** Override "today" (tests, other timezones). */
  today?: Date;
  /** First month shown. Defaults to the value's month, else today's. */
  defaultMonth?: Date;
  /** Limits for month navigation. */
  startMonth?: Date;
  endMonth?: Date;
  /** Focus the selected day (or today) on mount - used by the popovers. */
  autoFocus?: boolean;
  /** Range only: the Cancel button (the draft is discarded first). */
  onCancel?: () => void;
};

export type DatePickerContentProps = DatePickerSelection & DatePickerOptions;

const monthOf = (selection: DatePickerSelection) =>
  selection.type === 'range' ? selection.value?.from : selection.value;

/**
 * The inside of the Figma Date Picker (62:342): the Calendar, plus - for a
 * range - the footer with the summary ("14 – 20 Sep · 7 days"), Cancel and
 * Apply. A range is a draft until Apply; a third click starts a new range.
 * No surface of its own: DatePickerPanel and the popovers add it.
 */
export function DatePickerContent(props: DatePickerContentProps) {
  const { disabled, today, defaultMonth, startMonth, endMonth, autoFocus } = props;
  const calendar = {
    disabled,
    today,
    startMonth,
    endMonth,
    autoFocus,
    defaultMonth: defaultMonth ?? monthOf(props) ?? today,
  };
  if (props.type === 'range')
    return (
      <RangeContent
        value={props.value}
        onValueChange={props.onValueChange}
        onCancel={props.onCancel}
        calendar={calendar}
      />
    );
  const { value, onValueChange } = props;
  return (
    <Calendar
      {...calendar}
      mode="single"
      selected={value}
      // Picking the selected day again keeps it (no empty state by click).
      onSelect={(day) => onValueChange?.(day ?? value)}
    />
  );
}

function RangeContent({
  value,
  onValueChange,
  onCancel,
  calendar,
}: {
  value: DateRange | undefined;
  onValueChange?: (value: DateRange | undefined) => void;
  onCancel?: () => void;
  calendar: Omit<DatePickerOptions, 'onCancel'>;
}) {
  const [draft, setDraft] = useState(value);
  // A new value from outside replaces the draft.
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setDraft(value);
  }
  const complete = Boolean(draft?.from && draft.to);
  return (
    <div className="flex w-fit flex-col gap-2">
      <Calendar
        {...calendar}
        mode="range"
        // A lone start day is drawn as Selected, not as a half-banded start.
        selected={draft?.from && !draft.to ? { from: draft.from, to: draft.from } : draft}
        onSelect={(_, day) =>
          setDraft(
            !draft?.from || complete
              ? { from: day, to: undefined }
              : day < draft.from
                ? { from: day, to: draft.from }
                : { from: draft.from, to: day },
          )
        }
      />
      <div
        data-slot="date-picker-footer"
        className="flex items-center gap-2 border-t border-border-subtle pt-2"
      >
        <p
          aria-live="polite"
          className="text-body-sm text-text-secondary min-w-0 flex-1 truncate"
        >
          {formatRangeSummary(draft)}
        </p>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setDraft(value);
            onCancel?.();
          }}
        >
          Cancel
        </Button>
        <Button size="sm" disabled={!complete} onClick={() => onValueChange?.(draft)}>
          Apply
        </Button>
      </div>
    </div>
  );
}

/** Uncontrolled unless `value` is passed (then `onValueChange` must update it). */
function useSelection(props: DatePickerSelection): DatePickerSelection {
  const [single, setSingle] = useState<Date | undefined>(undefined);
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const controlled = 'value' in props && props.value !== undefined;
  if (props.type === 'range')
    return {
      type: 'range',
      value: controlled ? props.value : range,
      onValueChange: (v) => {
        setRange(v);
        props.onValueChange?.(v);
      },
    };
  return {
    type: 'single',
    value: controlled ? props.value : single,
    onValueChange: (v) => {
      setSingle(v);
      props.onValueChange?.(v);
    },
  };
}

export type DatePickerPanelProps = DatePickerContentProps & {
  className?: string;
  /** Names the panel (a group). */
  'aria-label'?: string;
};

/**
 * The Figma Date Picker (62:342) as a static panel: bg/surface, border/subtle,
 * radius/lg, Shadow/MD, 12px padding. Use it inline (a scheduling sidebar);
 * for a popover use DatePicker, or DateField for a typed + picked input.
 */
export function DatePickerPanel({
  className,
  'aria-label': ariaLabel,
  ...props
}: DatePickerPanelProps) {
  const selection = useSelection(props);
  const { type: _t, value: _v, onValueChange: _o, ...options } = props;
  return (
    <div
      role="group"
      aria-label={
        ariaLabel ?? (props.type === 'range' ? 'Choose dates' : 'Choose a date')
      }
      data-slot="date-picker"
      className={cn(datePickerPanel, className)}
    >
      <DatePickerContent {...options} {...selection} />
    </div>
  );
}

export type DatePickerProps = DatePickerContentProps & {
  /** The trigger - a Button, SelectButton, IconButton, … (rendered with `asChild`). */
  children: ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  /** Names the popup dialog. Defaults to "Choose a date" / "Choose dates". */
  'aria-label'?: string;
  /** Popover alignment against the trigger. */
  align?: 'start' | 'center' | 'end';
};

/**
 * Date Picker in a popover (4px below the trigger). Single closes when a day
 * is picked; Range closes on Apply or Cancel. Focus moves to the selected day
 * (or today) on open and back to the trigger on close; Escape closes.
 */
export function DatePicker({
  children,
  open: openProp,
  onOpenChange,
  defaultOpen = false,
  'aria-label': ariaLabel,
  align,
  ...props
}: DatePickerProps) {
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const selection = useSelection(props);
  const { type: _t, value: _v, onValueChange: _o, ...options } = props;
  const close =
    <T,>(fn?: (v: T) => void) =>
    (v: T) => {
      fn?.(v);
      setOpen(false);
    };
  const closing: DatePickerSelection =
    selection.type === 'range'
      ? { ...selection, onValueChange: close(selection.onValueChange) }
      : { ...selection, onValueChange: close(selection.onValueChange) };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align={align}
        aria-label={
          ariaLabel ?? (props.type === 'range' ? 'Choose dates' : 'Choose a date')
        }
        // The calendar focuses its day itself.
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="min-w-0 p-3"
      >
        <DatePickerContent
          {...options}
          {...closing}
          autoFocus
          onCancel={() => {
            options.onCancel?.();
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
